import React from 'react';

import { css } from '@emotion/react';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import Twitter from '@mui/icons-material/Twitter';
import YouTube from '@mui/icons-material/YouTube';
import { Grid, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import castArray from 'lodash/castArray';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';

import EmailSignup from './EmailSignup';
import { parseEmailToLink, parsePhoneToLink } from './utils';

const styles = css({
  ['.social-link-container']: {
    paddingTop: '1rem',
    // apply some padding between social links.
    ['> *']: {
      paddingRight: '1.5rem',
    },
  },
});

const smallScreenStyles = css({
  alignItems: 'center',
  padding: '2rem 0',
});

type Props = ClientSafeTenantConfig['footer']['contactInfoSection'] & {
  isSmallScreen?: boolean;
};

const InfoGridSection: React.FC<{ str: string; color: string }> = ({
  str,
  color,
}) => (
  <Grid>
    <Typography color={color} variant="body2">
      {str}
    </Typography>
  </Grid>
);
const InfoGridLink: React.FC<{
  str: string;
  color: string;
  usedFor: 'phone' | 'email';
}> = ({ str, color, usedFor }) => {
  return (
    <Link
      css={{ cursor: 'pointer' }}
      href={usedFor === 'phone' ? parsePhoneToLink(str) : parseEmailToLink(str)}
    >
      <InfoGridSection str={str} color={color} />
    </Link>
  );
};

const ContactInfoSection: React.FC<Props> = ({
  address,
  phoneNumber,
  email,
  socialLinks,
  isSmallScreen,
}) => {
  const { user } = useNonNullContext(UserContext);
  const isAuthenticated = user !== null;

  const theme = useTheme();
  const color = theme.palette.common.white;

  return (
    <Grid
      container
      direction="column"
      css={isSmallScreen ? [styles, smallScreenStyles] : styles}
    >
      {/* authed: display company contact info; unauthed, display email sign up */}
      {isAuthenticated ? (
        <>
          {/* @todo after code is up to prod and configs are updated to all use arrays, we can remove the castArray and just map over array entries. */}
          {castArray(address).map((a) => (
            <InfoGridSection str={a} key={a} color={color} />
          ))}
          <InfoGridLink str={phoneNumber} color={color} usedFor="phone" />
          <InfoGridLink str={email} color={color} usedFor="email" />
        </>
      ) : (
        <EmailSignup isSmallScreen={Boolean(isSmallScreen)} />
      )}
      <Grid>
        <Grid container direction="row" className="social-link-container">
          {socialLinks?.map((socialLink) => {
            return (
              <Link
                key={socialLink.hrefUrl}
                href={socialLink.hrefUrl}
                className="social-link"
                color={color}
              >
                {socialLink.logoAssetUrl === 'Facebook' && <Facebook />}
                {socialLink.logoAssetUrl === 'Instagram' && <Instagram />}
                {socialLink.logoAssetUrl === 'Twitter' && <Twitter />}
                {socialLink.logoAssetUrl === 'YouTube' && <YouTube />}
              </Link>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ContactInfoSection;
